/**
 * Download orchestration for photos/events.
 *
 * Tiers by shape:
 *  - a single photo  → direct presigned GET, saved immediately (no zip).
 *  - a multi-select or a whole event → a server-built ZIP job: start it, poll
 *    until ready, then download the one archive. The Lambda streams the zip to
 *    S3 (any size), so the browser never holds it in memory.
 *
 * Exposes live progress (`done`/`total`), a `phase` label, and a `cancel()` for
 * in-flight archive jobs so callers can render a proper progress UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getDownloadUrl,
  startArchive,
  getArchiveStatus,
  cancelArchive,
  type ArchiveState,
} from '../lib/apiClient.ts';
import { toast } from '../state/toastStore.ts';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // give up after 5 min

export type DownloadPhase = 'idle' | 'preparing' | 'zipping' | 'canceling';

/** Trigger a browser download of a URL as a file, without navigating away. */
function saveUrl(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useDownload() {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<DownloadPhase>('idle');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  // Guard against overlapping runs and post-unmount state updates.
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  // Job currently being polled + a flag so cancel() can stop the poll loop.
  const jobIdRef = useRef<string | null>(null);
  const canceledRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSet = useCallback((fn: () => void) => {
    if (mountedRef.current) fn();
  }, []);

  const reset = useCallback(() => {
    runningRef.current = false;
    jobIdRef.current = null;
    canceledRef.current = false;
    safeSet(() => {
      setBusy(false);
      setPhase('idle');
      setDone(0);
      setTotal(0);
    });
  }, [safeSet]);

  /** Download one original directly (no zip). */
  const downloadOne = useCallback(
    async (key: string, filename?: string) => {
      if (runningRef.current) return;
      runningRef.current = true;
      safeSet(() => {
        setBusy(true);
        setPhase('preparing');
      });
      try {
        const url = await getDownloadUrl(key);
        saveUrl(url, filename);
      } catch {
        toast.error('Could not download this photo');
      } finally {
        reset();
      }
    },
    [safeSet, reset],
  );

  /** Build + download a ZIP for a whole event or an explicit set of keys. */
  const downloadArchive = useCallback(
    async (args: { event?: string; keys?: string[] }) => {
      if (runningRef.current) return;
      runningRef.current = true;
      canceledRef.current = false;
      safeSet(() => {
        setBusy(true);
        setPhase('preparing');
        setDone(0);
        setTotal(0);
      });
      try {
        const job = await startArchive(args);
        jobIdRef.current = job.jobId;
        safeSet(() => {
          setTotal(job.total);
          setPhase('zipping');
        });

        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let state: ArchiveState | null = null;
        while (Date.now() < deadline && !canceledRef.current) {
          await sleep(POLL_INTERVAL_MS);
          if (canceledRef.current) break;
          state = await getArchiveStatus(job.jobId);
          safeSet(() => {
            setDone(state!.done);
            setTotal(state!.total);
          });
          if (state.status === 'ready' || state.status === 'error' || state.status === 'canceled') {
            break;
          }
        }

        if (canceledRef.current || state?.status === 'canceled') {
          toast.info('Download canceled');
        } else if (state?.status === 'ready' && state.downloadURL) {
          saveUrl(state.downloadURL, state.fileName);
          toast.success('Download ready');
        } else if (state?.status === 'error') {
          toast.error('Could not build the download');
        } else {
          toast.error('Download timed out — please try again');
        }
      } catch {
        toast.error('Could not start the download');
      } finally {
        reset();
      }
    },
    [safeSet, reset],
  );

  /** Ask the server to cancel the in-flight archive and stop polling. */
  const cancel = useCallback(() => {
    const id = jobIdRef.current;
    if (!id) return;
    canceledRef.current = true;
    safeSet(() => setPhase('canceling'));
    // Fire-and-forget; the poll loop already exits on canceledRef.
    cancelArchive(id).catch(() => {});
  }, [safeSet]);

  return { busy, phase, done, total, downloadOne, downloadArchive, cancel };
}
